import React, { useEffect, useState } from "react";
import {
  normalizeQuestDuration,
  normalizeQuestTime,
} from "../utils/questUtils";
import { getBrowserTimeZone, normalizeTimeZone, isValidTimeZone } from "../utils/timezoneUtils";

const buildInitialState = (profile = {}) => ({
  wakeTime: profile.wakeTime || "",
  sleepTime: profile.sleepTime || "",
  timeZone: profile.timeZone || getBrowserTimeZone(),
  longTermGoal: profile.longTermGoal || "",
  hunterProfile: profile.hunterProfile || "",
  questsPerDay:
    Number.isFinite(Number(profile.questsPerDay)) && Number(profile.questsPerDay) > 0
      ? Number(profile.questsPerDay)
      : 5,
  preferredFocusMinutes:
    Number.isFinite(Number(profile.preferredFocusMinutes)) && Number(profile.preferredFocusMinutes) > 0
      ? Number(profile.preferredFocusMinutes)
      : "",
  nonNegotiables: profile.nonNegotiables || "",
  frictionPoints: profile.frictionPoints || "",
});

const CONFIG = {
  setup: {
    label: "[ SYSTEM CALIBRATION ]",
    title: "Baseline Intake Required",
    description:
      "The System requires operational data before it can issue precise orders. Submit your baseline now.",
    submitLabel: "Finalize Calibration",
    savingLabel: "Finalizing Calibration...",
    hint:
      "These records become your permanent System baseline. The System will use them to issue daily orders, weekly directives, and future assessments.",
  },
  page: {
    label: "[ HUNTER PROFILE ]",
    title: "System Baseline Records",
    description:
      "Revise your current operating conditions. The System will adapt future directives to this profile.",
    submitLabel: "Commit Profile Revision",
    savingLabel: "Committing Revision...",
    hint:
      "Adjust these records when your routine, goals, or failure points change. Future System orders will recalibrate from this baseline.",
  },
};

const HunterProfileSetup = ({ profile, onSave, mode = "setup" }) => {
  const [form, setForm] = useState(buildInitialState(profile));
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const config = CONFIG[mode] || CONFIG.setup;

  useEffect(() => {
    setForm(buildInitialState(profile));
    if (mode !== "page") {
      setFeedback(null);
    }
    setSaving(false);
  }, [mode, profile]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (feedback) setFeedback(null);
    setForm((current) => ({ ...current, [name]: value }));
  };

  const validate = () => {
    if (!normalizeQuestTime(form.wakeTime)) {
      return "Wake-up target is required.";
    }
    if (!normalizeQuestTime(form.sleepTime)) {
      return "Sleep cutoff is required.";
    }
    if (!isValidTimeZone(form.timeZone)) {
      return "System timezone is required.";
    }
    if (!String(form.longTermGoal || "").trim()) {
      return "Declare a long-term objective before the System calibrates.";
    }
    if (!String(form.hunterProfile || "").trim()) {
      return "Tell the System who you are and what your current life looks like.";
    }
    const questsPerDay = Number(form.questsPerDay);
    if (!Number.isFinite(questsPerDay) || questsPerDay < 1 || questsPerDay > 10) {
      return "Daily quest load must be between 1 and 10.";
    }
    if (
      form.preferredFocusMinutes !== "" &&
      !normalizeQuestDuration(form.preferredFocusMinutes)
    ) {
      return "Preferred focus block must be between 15 and 360 minutes.";
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const error = validate();
    if (error) {
      setFeedback({
        tone: "warning",
        label: "[SYSTEM NOTICE]",
        text: error,
      });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      await Promise.resolve(
        onSave({
          wakeTime: normalizeQuestTime(form.wakeTime),
          sleepTime: normalizeQuestTime(form.sleepTime),
          timeZone: normalizeTimeZone(form.timeZone),
          longTermGoal: String(form.longTermGoal || "").trim(),
          hunterProfile: String(form.hunterProfile || "").trim(),
          questsPerDay: Math.max(1, Math.min(10, Math.round(Number(form.questsPerDay) || 5))),
          preferredFocusMinutes: normalizeQuestDuration(form.preferredFocusMinutes),
          nonNegotiables: String(form.nonNegotiables || "").trim(),
          frictionPoints: String(form.frictionPoints || "").trim(),
          onboardingCompleted: true,
        })
      );
      setFeedback(
        mode === "page"
          ? {
              tone: "success",
              label: "[SYSTEM UPDATE]",
              text: "Baseline records revised. Future directives will adapt.",
            }
          : null
      );
      setSaving(false);
    } catch (error) {
      console.error("Hunter profile setup failed:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text:
          error?.message ||
          "Calibration data could not be stored. Try again.",
      });
      setSaving(false);
    }
  };

  const formContent = (
    <div className={mode === "page" ? "setup-card setup-card--page" : "login-card setup-card"}>
      <div className="setup-card__header">
        <div className="setup-card__label">{config.label}</div>
        <h1>{config.title}</h1>
        <p className="login-card__copy setup-card__copy">{config.description}</p>
      </div>

      {feedback && (
        <div
          className={`system-feedback system-feedback--${feedback.tone}`}
          role={feedback.tone === "error" ? "alert" : "status"}
          aria-live={feedback.tone === "error" ? "assertive" : "polite"}
        >
          <div className="system-feedback__label">{feedback.label}</div>
          <div className="system-feedback__text">{feedback.text}</div>
        </div>
      )}

      <form className="auth-form setup-form" onSubmit={handleSubmit} noValidate>
        <div className="setup-form__grid">
          <div className="auth-form__field">
            <label htmlFor={`setup-wake-time-${mode}`}>Wake-up target</label>
            <input
              id={`setup-wake-time-${mode}`}
              name="wakeTime"
              type="time"
              value={form.wakeTime}
              onChange={handleChange}
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor={`setup-sleep-time-${mode}`}>Sleep cutoff</label>
            <input
              id={`setup-sleep-time-${mode}`}
              name="sleepTime"
              type="time"
              value={form.sleepTime}
              onChange={handleChange}
            />
          </div>

          <div className="auth-form__field setup-form__wide">
            <label htmlFor={`setup-time-zone-${mode}`}>System timezone</label>
            <input
              id={`setup-time-zone-${mode}`}
              name="timeZone"
              type="text"
              value={form.timeZone}
              onChange={handleChange}
              placeholder="e.g. Asia/Kolkata"
              autoComplete="off"
            />
          </div>

          <div className="auth-form__field setup-form__wide">
            <label htmlFor={`setup-hunter-profile-${mode}`}>About you</label>
            <textarea
              id={`setup-hunter-profile-${mode}`}
              name="hunterProfile"
              rows={4}
              value={form.hunterProfile}
              onChange={handleChange}
              placeholder="Describe your current life, responsibilities, strengths, and pressure points."
            />
          </div>

          <div className="auth-form__field setup-form__wide">
            <label htmlFor={`setup-long-term-goal-${mode}`}>Long-term objective</label>
            <textarea
              id={`setup-long-term-goal-${mode}`}
              name="longTermGoal"
              rows={3}
              value={form.longTermGoal}
              onChange={handleChange}
              placeholder="What are you trying to become, build, achieve, or recover over the next months?"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor={`setup-quests-per-day-${mode}`}>Daily quest load</label>
            <input
              id={`setup-quests-per-day-${mode}`}
              name="questsPerDay"
              type="number"
              min={1}
              max={10}
              step={1}
              value={form.questsPerDay}
              onChange={handleChange}
              placeholder="e.g. 5"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor={`setup-focus-minutes-${mode}`}>Preferred focus block (minutes)</label>
            <input
              id={`setup-focus-minutes-${mode}`}
              name="preferredFocusMinutes"
              type="number"
              min={15}
              max={360}
              step={15}
              value={form.preferredFocusMinutes}
              onChange={handleChange}
              placeholder="e.g. 90"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor={`setup-friction-points-${mode}`}>What usually derails you?</label>
            <textarea
              id={`setup-friction-points-${mode}`}
              name="frictionPoints"
              rows={3}
              value={form.frictionPoints}
              onChange={handleChange}
              placeholder="Phone drift, late sleep, overplanning, energy crashes, missed study windows..."
            />
          </div>

          <div className="auth-form__field setup-form__wide">
            <label htmlFor={`setup-non-negotiables-${mode}`}>Non-negotiable rules</label>
            <textarea
              id={`setup-non-negotiables-${mode}`}
              name="nonNegotiables"
              rows={3}
              value={form.nonNegotiables}
              onChange={handleChange}
              placeholder="Rules the System should respect, e.g. class schedule, job hours, prayer, gym after work, no late caffeine."
            />
          </div>
        </div>

        <p className="auth-form__hint setup-form__hint">{config.hint}</p>

        <button className="login-btn auth-form__submit" type="submit" disabled={saving}>
          {saving ? config.savingLabel : config.submitLabel}
        </button>
      </form>
    </div>
  );

  if (mode === "page") {
    return formContent;
  }

  return (
    <>
      <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />
      <div className="login-bg setup-bg">{formContent}</div>
    </>
  );
};

export default HunterProfileSetup;
